import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { commentApi, CreateCommentDTO, UpdateCommentDTO } from '../../../../../apis/comment.api';
import { useAtomValue } from 'jotai';
import { profileAtom } from '../../../../../components/Settings/atom';
import { useEffect, useState } from 'react';
import { debounce } from 'lodash';

export const useGeneralComments = (
  articleId: string,
  sortValue?: string,
  initialLimit: number = 6,
  infiniteLimit: number = 10,
  notificationCommentId?: string
) => {
  const profile = useAtomValue(profileAtom);
  const queryClient = useQueryClient();
  const [hasClickedLoadMore, setHasClickedLoadMore] = useState(false);

  // Use a single infinite query but control its behavior
  const {
    data: generalComments = [] as any,
    isLoading: generalCommentsLoading,
    error: generalCommentsError,
    fetchNextPage: fetchNextPageGeneralComments,
    hasNextPage: hasNextPageGeneralComments,
    isFetchingNextPage: isFetchingNextPageGeneralComments,
    refetch: refetchGeneralComments
  } = useInfiniteQuery({
    queryKey: ['generalComments', articleId, profile.id, sortValue, notificationCommentId, hasClickedLoadMore],
    queryFn: ({ pageParam = 1 }) => {
      // Use initial limit for first page, infinite limit for subsequent pages
      const limit = pageParam === 1 && !hasClickedLoadMore ? initialLimit : infiniteLimit;
      return sortValue === 'mostRelevant'
        ? commentApi.getRelevantComments(articleId, null, true, limit, pageParam, profile.id)
        : commentApi.getComments(
            articleId,
            null,
            true,
            limit,
            pageParam,
            profile.id,
            notificationCommentId
          );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      // If we haven't clicked load more and this is the first page, don't auto-fetch more
      if (!hasClickedLoadMore && allPages.length === 1) {
        // Only return next page param if there are more comments than our initial limit
        return lastPage.data.length >= initialLimit ? 2 : undefined;
      }
      // Normal infinite scroll behavior after load more is clicked
      const limit = infiniteLimit;
      return lastPage.data.length < limit ? undefined : allPages.length + 1;
    }
  });

  const createGeneralCommentMutation = useMutation<any, Error, any>({
    mutationFn: commentApi.createGeneralComment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['create', articleId]
      });
    }
  });

  const updateCommentMutation = useMutation<any, Error, UpdateCommentDTO>({
    mutationFn: commentApi.updateComment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['update']
      });
    }
  });

  const deleteCommentMutation = useMutation<any, Error, string>({
    mutationFn: commentApi.deleteComment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['delete']
      });
    }
  });

  const reactToCommentMutation = useMutation<
    any,
    Error,
    { commentId: string; nodeId: string; reactionType: string; isLike: boolean }
  >({
    mutationFn: commentApi.reactToComment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['react']
      });
    }
  });

  // Handle infinite scroll only after "Load More" has been clicked
  const handleScrollGeneral = debounce(() => {
    if (!hasClickedLoadMore) return; // Don't auto-scroll until Load More is clicked

    const scrollPosition = window.scrollY + window.innerHeight;
    const scrollThreshold = document.documentElement.scrollHeight - 1000;
    const nearBottom = scrollPosition >= scrollThreshold;

    if (nearBottom && hasNextPageGeneralComments && !isFetchingNextPageGeneralComments) {
      fetchNextPageGeneralComments();
    }
  }, 200);

  // Handle Load More button click
  const handleLoadMore = () => {
    setHasClickedLoadMore(true);
    // Fetch the next page when load more is clicked
    fetchNextPageGeneralComments();
  };

  // Reset state when sort value changes
  useEffect(() => {
    setHasClickedLoadMore(false);
  }, [sortValue]);

  // Only add scroll listener after Load More is clicked
  useEffect(() => {
    if (hasClickedLoadMore) {
      window.addEventListener('scroll', handleScrollGeneral);
      return () => {
        window.removeEventListener('scroll', handleScrollGeneral);
        handleScrollGeneral.cancel();
      };
    }
  }, [hasClickedLoadMore, hasNextPageGeneralComments, isFetchingNextPageGeneralComments]);

  // Calculate if we should show the Load More button
  const shouldShowLoadMore = !hasClickedLoadMore && 
    !generalCommentsLoading && 
    hasNextPageGeneralComments;

  return {
    generalComments,
    generalCommentsLoading,
    generalCommentsError,
    createGeneralComment: createGeneralCommentMutation.mutateAsync,
    isCreatingGeneralComment: createGeneralCommentMutation.isPending,
    updateComment: updateCommentMutation.mutateAsync,
    isUpdatingComment: updateCommentMutation.isPending,
    deleteComment: deleteCommentMutation.mutateAsync,
    reactToComment: reactToCommentMutation.mutateAsync,
    isReacting: reactToCommentMutation.isPending,
    refetchGeneralComments,
    isFetchingNextPageGeneralComments,
    hasClickedLoadMore,
    shouldShowLoadMore,
    handleLoadMore
  };
};